import React, { useEffect, useState } from "react";
import { getFirstNations } from "../../lib/api";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";
import { Check, ChevronsUpDown, Loader2, AlertCircle, RotateCcw } from "lucide-react";

// Shared across mounts so navigating back/forth in the wizard doesn't re-flash the loader.
// Each item is { id, name }.
let cachedItems = null;
let inFlight = null;

const loadItems = () => {
  if (cachedItems) return Promise.resolve(cachedItems);
  if (!inFlight) {
    inFlight = getFirstNations()
      .then((res) => {
        cachedItems = res.firstNations || [];
        return cachedItems;
      })
      .catch((e) => {
        inFlight = null; // allow retry
        throw e;
      });
  }
  return inFlight;
};

export default function FirstNationSelect({ value, onChange, invalid }) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState(cachedItems || []);
  const [loading, setLoading] = useState(!cachedItems);
  const [failed, setFailed] = useState(false);

  const fetchOnce = () => {
    setLoading(true);
    setFailed(false);
    loadItems()
      .then((items) => setOptions(items))
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!cachedItems) fetchOnce();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (failed) {
    return (
      <div
        data-testid="firstNation-load-error"
        className="flex items-center justify-between gap-2 rounded-md border border-destructive/50 bg-destructive/5 px-3 py-2"
      >
        <span className="flex items-center gap-2 text-sm text-destructive">
          <AlertCircle className="h-4 w-4" /> Couldn't load the official list.
        </span>
        <Button type="button" variant="outline" size="sm" onClick={fetchOnce} data-testid="firstNation-retry-btn" className="gap-1.5">
          <RotateCcw className="h-3.5 w-3.5" /> Retry
        </Button>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid="lead-firstNation-select"
          className={`w-full justify-between font-normal ${!value ? "text-muted-foreground" : "text-foreground"} ${invalid ? "border-destructive" : ""}`}
        >
          {loading ? (
            <span className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading official names…
            </span>
          ) : (
            value || "Select your First Nation"
          )}
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search official First Nations…" data-testid="firstNation-search-input" />
          <CommandList>
            <CommandEmpty>No matching official First Nation found.</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt.id}
                  value={opt.name}
                  data-testid={`firstNation-option-${opt.name}`}
                  onSelect={() => {
                    // cmdk lowercases the passed value; use the option's real fields.
                    onChange(opt.name === value ? null : { id: opt.id, name: opt.name });
                    setOpen(false);
                  }}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === opt.name ? "opacity-100" : "opacity-0"}`} />
                  {opt.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
